import React, { useState, useCallback, useRef } from 'react';
import FileUpload from './components/FileUpload';
import AudioPlayer, { AudioPlayerRef } from './components/AudioPlayer';
import SegmentEditor from './components/SegmentEditor';
import { AudioData, AudioSegment, SegmentData } from './types';

const App: React.FC = () => {
  const [audioData, setAudioData] = useState<AudioData | null>(null);
  const [segments, setSegments] = useState<AudioSegment[]>([]);
  const [currentSegment, setCurrentSegment] = useState<AudioSegment | null>(
    null
  );
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [jsonFile, setJsonFile] = useState<File | null>(null);
  const audioPlayerRef = useRef<AudioPlayerRef>(null);

  const handleAudioUpload = useCallback((file: File) => {
    console.log('رفع ملف الصوت:', file.name, file.type, file.size);
    setAudioFile(file);

    // Clear previous audio data
    setAudioData(null);

    const url = URL.createObjectURL(file);
    console.log('تم إنشاء رابط الكائن:', url);

    const audio = new Audio();

    const handleLoadedMetadata = () => {
      console.log('تم تحميل بيانات الصوت. المدة:', audio.duration);
      setAudioData({
        file,
        url,
        duration: audio.duration,
      });
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('canplaythrough', handleCanPlay);
    };

    const handleError = (e: Event) => {
      console.error('خطأ في تحميل الصوت:', e);
      console.error('تفاصيل خطأ الصوت:', audio.error);
      alert(
        `خطأ في تحميل ملف الصوت: ${
          audio.error?.message || 'خطأ غير معروف'
        }. يرجى تجربة ملف آخر.`
      );
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('canplaythrough', handleCanPlay);
    };

    const handleCanPlay = () => {
      console.log('يمكن تشغيل الصوت. المدة:', audio.duration);
      if (audio.duration && !isNaN(audio.duration)) {
        setAudioData({
          file,
          url,
          duration: audio.duration,
        });
      }
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('error', handleError);
    audio.addEventListener('canplaythrough', handleCanPlay);

    audio.src = url;
    audio.preload = 'metadata';
    audio.load();
  }, []);

  const handleJsonUpload = useCallback((file: File) => {
    setJsonFile(file);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const data = JSON.parse(content);

        let loadedSegments: AudioSegment[] = [];

        if (Array.isArray(data)) {
          loadedSegments = data.map((item, index) => ({
            id: `segment-${index}`,
            startTime: item.start || item.startTime || 0,
            endTime: item.end || item.endTime || 0,
            text: item.text || item.transcript || '',
            speaker: item.speaker || undefined,
          }));
        } else if (data.segments && Array.isArray(data.segments)) {
          loadedSegments = data.segments.map((item: any, index: number) => ({
            id: `segment-${index}`,
            startTime: item.start || item.startTime || 0,
            endTime: item.end || item.endTime || 0,
            text: item.text || item.transcript || '',
            speaker: item.speaker || undefined,
          }));
        } else {
          console.error('تنسيق JSON غير صحيح');
          return;
        }

        setSegments(loadedSegments);
      } catch (error) {
        console.error('خطأ في تحليل ملف JSON:', error);
      }
    };

    reader.readAsText(file);
  }, []);

  const handleTimeUpdate = useCallback(
    (currentTime: number) => {
      // Find active segment with a small tolerance to handle timing precision
      const activeSegment = segments.find(
        (segment) =>
          currentTime >= (segment.startTime - 0.1) && currentTime <= (segment.endTime + 0.1)
      );
      
      // Only update if the segment actually changed to avoid unnecessary re-renders
      if (activeSegment?.id !== currentSegment?.id) {
        setCurrentSegment(activeSegment || null);
      }
    },
    [segments, currentSegment]
  );

  const handleSegmentClick = useCallback((segment: AudioSegment) => {
    setCurrentSegment(segment);
  }, []);

  const handlePlaySegment = useCallback((segment: AudioSegment) => {
    if (audioPlayerRef.current) {
      audioPlayerRef.current.playSegment(segment);
    }
  }, []);

  const handleSegmentUpdate = useCallback(
    (updatedSegment: AudioSegment) => {
      // Validate segment times
      let validatedSegment = { ...updatedSegment };

      // Ensure start time is not negative
      validatedSegment.startTime = Math.max(0, validatedSegment.startTime);

      // Ensure end time doesn't exceed audio duration
      if (audioData && validatedSegment.endTime > audioData.duration) {
        validatedSegment.endTime = audioData.duration;
      }

      // Ensure end time is after start time
      if (validatedSegment.endTime <= validatedSegment.startTime) {
        validatedSegment.endTime = validatedSegment.startTime + 0.1;
      }

      setSegments((prev) =>
        prev.map((segment) =>
          segment.id === validatedSegment.id ? validatedSegment : segment
        )
      );
    },
    [audioData]
  );

  const handleSegmentDelete = useCallback(
    (segmentId: string) => {
      setSegments((prev) => prev.filter((segment) => segment.id !== segmentId));
      if (currentSegment?.id === segmentId) {
        setCurrentSegment(null);
      }
    },
    [currentSegment]
  );

  const handleSegmentAdd = useCallback(() => {
    // Sort existing segments by start time
    const sortedSegments = [...segments].sort(
      (a, b) => a.startTime - b.startTime
    );

    let startTime = 0;
    let endTime = 5;

    // If there are existing segments, place new segment after the last one
    if (sortedSegments.length > 0) {
      const lastSegment = sortedSegments[sortedSegments.length - 1];
      startTime = lastSegment.endTime;
      endTime = Math.min(startTime + 5, audioData?.duration || startTime + 5);
    } else if (audioData) {
      // If no segments exist, start from 0 but don't exceed audio duration
      endTime = Math.min(5, audioData.duration);
    }

    // Ensure end time doesn't exceed audio duration
    if (audioData && endTime > audioData.duration) {
      endTime = audioData.duration;
      startTime = Math.max(0, endTime - 5);
    }

    const newSegment: AudioSegment = {
      id: `segment-${Date.now()}`,
      startTime,
      endTime,
      text: '',
      speaker: undefined,
    };
    setSegments((prev) => [...prev, newSegment]);
  }, [segments, audioData]);

  const handleExport = useCallback(() => {
    const exportData: SegmentData = {
      segments: segments.map((segment) => ({
        id: segment.id,
        startTime: segment.startTime,
        endTime: segment.endTime,
        text: segment.text,
        speaker: segment.speaker,
      })),
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataUri =
      'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);

    const exportFileDefaultName = audioFile?.name
      ? `${audioFile.name.split('.').slice(0, -1).join('.')}_export.json`
      : 'export.json';
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  }, [segments]);

  return (
    <div className='container'>
      <h1>استوديو تسمية الصوت</h1>
      <p>
        ارفع ملفات الصوت وأنشئ مقاطع مُسمّاة لإنشاء مجموعة بيانات التعرف على
        الكلام.
      </p>

      <FileUpload
        onAudioUpload={handleAudioUpload}
        onJsonUpload={handleJsonUpload}
        audioFile={audioFile}
        jsonFile={jsonFile}
      />

      <AudioPlayer
        ref={audioPlayerRef}
        audioData={audioData}
        segments={segments}
        currentSegment={currentSegment}
        onTimeUpdate={handleTimeUpdate}
        onSegmentClick={handleSegmentClick}
      />

      <SegmentEditor
        segments={segments}
        currentSegment={currentSegment}
        onSegmentUpdate={handleSegmentUpdate}
        onSegmentDelete={handleSegmentDelete}
        onSegmentAdd={handleSegmentAdd}
        onSegmentSelect={handleSegmentClick}
        onPlaySegment={handlePlaySegment}
      />

      {segments.length > 0 && (
        <div
          style={{ marginTop: '20px', textAlign: 'center', direction: 'rtl' }}
        >
          <button
            onClick={handleExport}
            className='btn btn-primary'
          >
            تصدير البيانات المُسمّاة
          </button>
        </div>
      )}
    </div>
  );
};

export default App;
